import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { Box, Grid, IconButton, Tooltip } from "@mui/material";
import dayjs from "dayjs";
import { FormikProps } from "formik";
import { useState } from "react";
import { COMMUNICATION_TYPE } from "../../../../../constants/master";
import { NewPatientForm } from "../../../../../constants/provider/patient/add-new-patient/add-new-patient-form";
import { useProviderListV2WithoutReferralFilter } from "../../../../../queries/use-provider-list-v2";
import { useLocationOptionsV2 } from "@/queries/use-location-options-v2";
import { DEF_PAGINATION } from "../../../../../utils/utils";
import CustomDatePicker from "../../../../CustomDatePicker";
import CustomSelect from "../../../../CustomSelect";
import Flex from "../../../../Flex";
import OutlineCard from "../../../../OutlineCard";
import CustomTitle from "../../../CustomTitle";
import NewReferringProvider from "../../../../../pages/app/provider/collabration/referrals/NewReferringProvider";
import { getStoredRealm } from "@/utils/auth-storage";

interface ProviderInformationProps {
  formik: FormikProps<NewPatientForm>;
}

const ProviderInformation = ({ formik }: ProviderInformationProps) => {
  const [searchProviderKey, setSearchProviderKey] = useState<string>("");
  const [searchProviderGroupKey, setSearchProviderGroupKey] =
    useState<string>("");
  const [isAddPcpOpen, setIsAddPcpOpen] = useState<boolean>(false);
  const { values, setFieldValue } = formik;
  const xTENANTID = getStoredRealm() ?? "";
  const { data: providersList } = useProviderListV2WithoutReferralFilter({
    pagination: DEF_PAGINATION,
    status: true,
    searchString: searchProviderKey,
    xTENANTID,
  });

  const { data: providerGroupLocationList } = useLocationOptionsV2({
    searchString: searchProviderGroupKey,
    xTENANTID,
  });

  return (
    <Box id={"Provider Information"}>
      <CustomTitle mb={1}>Provider Information</CustomTitle>

      <OutlineCard>
        <Grid
          container
          spacing={2}
          sx={{
            p: 0.8,
            flexDirection: "column",
          }}
        >
          <Grid container spacing={2}>
            <Grid size={3}>
              <CustomSelect
                label="Provider Group Location"
                // placeholder="Select Provider Group Location"
                options={providerGroupLocationList?.data?.map((it) => ({
                  label: it.name,
                  value: it.uuid,
                }))}
                onSearch={(_, val) => setSearchProviderGroupKey(val)}
                value={Object.keys(values.locationId ?? {})?.[0] ?? undefined}
                onChange={(value: string | null) => {
                  const curOption = providerGroupLocationList?.data?.find(
                    (it) => it.uuid === value
                  );

                  // clear provider after clear location
                  if (!value) {
                    setFieldValue("providerId", undefined);
                  }

                  setFieldValue(
                    "locationId",
                    curOption
                      ? {
                          [curOption.uuid]: curOption.name,
                        }
                      : undefined
                  );
                }}
              />
            </Grid>
            <Grid size={3}>
              <Flex alignItems="flex-end" gap={0.5}>
                <Box
                  sx={{
                    flex: 1,
                  }}
                >
                  <CustomSelect
                    label="Primary Care Provider (PCP)"
                    options={providersList?.data?.content?.map((it) => ({
                      label: it?.name,
                      value: it.uuid,
                    }))}
                    onSearch={(_, val) => setSearchProviderKey(val)}
                    value={Object.keys(values.providerId ?? {})?.[0]}
                    onChange={(value: string | null) => {
                      const curOption = providersList?.data?.content?.find(
                        (it) => it.uuid === value
                      );
                      setFieldValue(
                        "providerId",
                        curOption
                          ? {
                              [curOption?.uuid]: curOption?.name,
                            }
                          : undefined
                      );
                    }}
                  />
                </Box>
                <Tooltip title="Add New PCP">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => setIsAddPcpOpen(true)}
                    sx={{ mb: 0.5 }}
                  >
                    <PersonAddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Flex>
            </Grid>
            <Grid size={3}>
              <CustomDatePicker
                label="Registration Date"
                maxDate={dayjs(new Date())}
                value={
                  values.registrationDate
                    ? dayjs(values.registrationDate)
                    : null
                }
                onChange={(value) => setFieldValue("registrationDate", value)}
                // placeholder="Choose Date"
              />
            </Grid>
            <Grid size={3}>
              <CustomSelect
                options={COMMUNICATION_TYPE}
                value={values.defaultCommunicationChannel}
                onChange={(value) => {
                  setFieldValue("defaultCommunicationChannel", value);
                }}
                label="Default Communication Channel"
                // required={true}
                // error={touched?.maritalStatus && errors?.maritalStatus}
              />
            </Grid>
          </Grid>
        </Grid>
      </OutlineCard>

      {isAddPcpOpen && (
        <NewReferringProvider
          open={isAddPcpOpen}
          onClose={() => setIsAddPcpOpen(false)}
          referralInOut="Receiving"
        />
      )}
    </Box>
  );
};

export default ProviderInformation;
